import { useEffect } from "react";

/**
 * Lightweight SEO hook — sets <title>, <meta name="description">,
 * Open Graph / Twitter meta, and canonical URL on every page render.
 * Avoids the bundle weight of react-helmet-async while giving the same outcome.
 *
 * Usage:
 *   useSEO({
 *     title: "Joy Pearl — Artificial Jewellery & Kids' Gifts | Official Store",
 *     description: "...",
 *     image: "https://www.vskstore.com/og-image.jpg",
 *     path: "/products/foo",
 *   });
 */

const SITE_NAME = "Joy Pearl";
const SITE_BASE = "https://www.vskstore.com";
const DEFAULT_OG_IMAGE = `${SITE_BASE}/og-image.jpg`;
const DEFAULT_DESCRIPTION =
  "Joy Pearl — India's trusted marketplace for handcrafted artificial jewellery, earrings, necklaces, bangles & kids' gifts. COD available across India. Shop online at vskstore.com.";

function setMeta(attr, key, value) {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSEO({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_OG_IMAGE,
  path,
  type = "website",
  noIndex = false,
} = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title}` : `${SITE_NAME} — Artificial Jewellery & Kids' Gifts`;
    document.title = fullTitle;

    const url = SITE_BASE + (path || window.location.pathname);

    setMeta("name", "description", description);
    setMeta("name", "robots", noIndex ? "noindex, nofollow" : "index, follow");

    // Open Graph
    setMeta("property", "og:type", type);
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", image);
    setMeta("property", "og:locale", "en_IN");

    // Twitter
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);

    setLink("canonical", url);
  }, [title, description, image, path, type, noIndex]);
}
